/* Imperia cart layer — in-cart product cards, quantity steppers, sticky checkout bar.
   Deliberately no MutationObserver: everything re-renders off cart events only.

   The UI is optimistic: a click repaints the button, the stepper and the sticky bar
   immediately from a locally-adjusted copy of the cart, and the network reconciles
   behind it. `serverCart` is the last cart Shopify confirmed; `lastCart` is what the
   screen currently shows (usually the same, briefly ahead of it after a click).
   Rapid clicks coalesce into `desired` (variantId -> absolute target quantity) and
   drain one request at a time, so five taps on + cost one or two requests, not five. */
(function () {
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CURRENCY = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  var bar = null;

  function money(cents) {
    try {
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: CURRENCY });
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function getCart() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  /* Impact's own cart:change carries pre-rendered section HTML in detail.cart.sections,
     which a plain /cart.js response does not have — dispatching it with a bare cart made
     CartDrawer throw and leave a stale (usually empty) drawer behind. cart:refresh asks
     every Impact listener to re-fetch for itself instead, which is what we want. */
  var selfRefresh = false;

  function announce(cart) {
    render(cart);
    selfRefresh = true;
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    selfRefresh = false;
  }

  /* ---------------------------------------------------------------- sticky bar */

  /* On product pages the bar runs in product mode: an always-visible Add-to-cart
     segment for the product being viewed, with the checkout segment appearing
     beside it only while the cart holds anything. Elsewhere it stays checkout-only. */
  var PRODUCT_HANDLE = null;
  if (/\/products\//.test(location.pathname)) {
    PRODUCT_HANDLE = location.pathname.split('/products/')[1].split('/')[0].split('?')[0] || null;
  }

  function mainVariantInput() {
    return document.querySelector('.product-info form[is="product-form"] input[name="id"]') ||
      document.querySelector('form[is="product-form"] input[name="id"]');
  }

  function checkoutFormHtml() {
    /* A form POST to /cart with a submit named "checkout" is how Shopify's own cart
       reaches checkout — it is what Impact's checkout button does. An <a href="/checkout">
       is easy for other click handlers to swallow; a form submit is not. */
    /* The hidden checkout input mirrors the submit button's param, so a programmatic
       form.submit() (which skips the button) still reaches checkout — see maybeCheckout. */
    return '<form class="imperia-cart-bar__form" action="' + ROOT + 'cart" method="post">' +
      '<input type="hidden" name="checkout" value="">' +
      '<button type="submit" name="checkout" class="imperia-cart-bar__link">' +
      '<span class="imperia-cart-bar__text">' +
      '<span class="imperia-cart-bar__label">Checkout</span>' +
      '<span class="imperia-cart-bar__total"></span>' +
      '</span>' +
      '<span class="imperia-cart-bar__count">0</span>' +
      '</button>' +
      '</form>';
  }

  function ensureBar() {
    if (bar) return bar;
    bar = document.createElement('div');
    bar.className = 'imperia-cart-bar';
    bar.hidden = true;
    if (PRODUCT_HANDLE && mainVariantInput()) {
      bar.className += ' imperia-cart-bar--product';
      bar.innerHTML =
        '<div class="imperia-cart-bar__row">' +
        '<button type="button" class="imperia-cart-bar__atc">' +
        '<span class="imperia-cart-bar__atc-label">Add to cart</span>' +
        '<span class="imperia-cart-bar__atc-price"></span>' +
        '</button>' +
        checkoutFormHtml() +
        '</div>';
      refreshAtcPrice();
    } else {
      bar.innerHTML = checkoutFormHtml();
    }
    document.body.appendChild(bar);
    return bar;
  }

  function refreshAtcPrice() {
    if (!PRODUCT_HANDLE || !bar) return;
    var input = mainVariantInput();
    if (!input || !input.value) return;
    var variantId = input.value;
    productData(PRODUCT_HANDLE).then(function (product) {
      var el = bar && bar.querySelector('.imperia-cart-bar__atc-price');
      if (!el) return;
      var variant = (product.variants || []).filter(function (v) { return String(v.id) === String(variantId); })[0];
      if (variant) el.textContent = money(variant.price);
    });
  }

  function renderBar(cart) {
    var b = ensureBar();
    var count = cart.item_count || 0;
    var productMode = b.classList.contains('imperia-cart-bar--product');
    b.hidden = productMode ? false : count === 0;
    b.classList.toggle('imperia-cart-bar--has-items', count > 0);
    document.body.classList.toggle('imperia-has-cart-bar', productMode || count > 0);
    if (!count) return;
    b.querySelector('.imperia-cart-bar__total').textContent = money(cart.total_price || 0);
    b.querySelector('.imperia-cart-bar__count').textContent = String(count);
  }

  /* -------------------------------------------------------------- product cards */

  function indexCart(cart) {
    var byVariant = {};
    var byHandle = {};
    var variantForHandle = {};
    (cart.items || []).forEach(function (item) {
      byVariant[item.variant_id] = (byVariant[item.variant_id] || 0) + item.quantity;
      byHandle[item.handle] = (byHandle[item.handle] || 0) + item.quantity;
      if (!variantForHandle[item.handle]) variantForHandle[item.handle] = String(item.variant_id);
    });
    return { byVariant: byVariant, byHandle: byHandle, variantForHandle: variantForHandle };
  }

  function handleOf(node) {
    var card = node.closest('.product-card, .imperia-fav-card');
    var link = card && card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    /* Split rather than match: this file is pushed through a text transport that has
       mangled escaped regexes before, and the result here is identical. */
    var href = link.getAttribute('href').split('?')[0].split('#')[0];
    var parts = href.split('/products/');
    if (parts.length < 2) return null;
    return parts[1].split('/')[0] || null;
  }

  function stepper(qty, variantId) {
    if (!variantId) {
      return '<span class="imperia-buy-bar__qty">' + qty + ' in bag</span>' +
        '<span class="imperia-buy-bar__plus" aria-hidden="true">+</span>';
    }
    return '<button type="button" class="imperia-buy-bar__step" data-step="-1" data-variant="' + variantId + '" aria-label="Remove one">&minus;</button>' +
      '<span class="imperia-buy-bar__qty">' + qty + '</span>' +
      '<button type="button" class="imperia-buy-bar__step" data-step="1" data-variant="' + variantId + '" aria-label="Add one">+</button>';
  }

  function renderCards(cart) {
    var idx = indexCart(cart);

    document.querySelectorAll('.imperia-buy-bar').forEach(function (holder) {
      var button = holder.querySelector('.imperia-buy-bar__button');
      if (!button) return;
      var content = button.querySelector(':scope > div') || button;

      if (!button.dataset.imperiaDefault) {
        button.dataset.imperiaDefault = content.innerHTML;
      }

      var input = holder.querySelector('input[name="id"]');
      var card = holder.closest('.product-card');
      var swatch = card && card.querySelector('input[data-variant-id]:checked');
      var variantId = (input && input.value) || (swatch && swatch.getAttribute('data-variant-id')) || null;
      var handle = handleOf(holder);
      var qty = 0;

      if (variantId && idx.byVariant[variantId] != null) {
        qty = idx.byVariant[variantId];
      } else if (handle && idx.byHandle[handle] != null) {
        /* A different colourway of the same product is in the cart — step that line */
        qty = idx.byHandle[handle];
        variantId = idx.variantForHandle[handle] || variantId;
      }

      if (qty > 0) {
        var next = stepper(qty, variantId);
        if (content.innerHTML !== next) content.innerHTML = next;
        button.classList.add('is-in-cart');
        /* Set inline: Impact's own card rules otherwise win the cascade on this button */
        button.style.background = '#1a1714';
      } else {
        if (button.classList.contains('is-in-cart')) {
          content.innerHTML = button.dataset.imperiaDefault;
        }
        button.classList.remove('is-in-cart');
        button.style.background = '';
      }
    });
  }

  var lastCart = null;   // what the screen shows (may be optimistically ahead)
  var serverCart = null; // what Shopify last confirmed

  function render(cart) {
    if (!cart) return;
    lastCart = cart;
    renderBar(cart);
    renderCards(cart);
  }

  /* Repaint newly-inserted cards from the cart we already hold.
     imperia-collection.js appends a page of products at a time and needs those cards to
     show their quantity steppers; it used to ask for that with a cart:refresh, which costs
     a /cart.js round trip AND makes Impact re-fetch the rendered cart-drawer section — once
     per appended page. Nothing about the cart changed, so none of that was needed. */
  window.ImperiaCart = {
    repaint: function () {
      if (lastCart) renderCards(lastCart);
      else getCart().then(function (cart) { serverCart = cart; render(cart); });
    }
  };

  /* ------------------------------------------------- optimistic write pipeline */

  var desired = {};      // variantId -> absolute target quantity, not yet confirmed
  var inflight = false;
  var pendingCheckout = false;

  function pipelineIdle() {
    return !inflight && Object.keys(desired).length === 0;
  }

  /* The optimistic UI shows the checkout segment the instant an add is clicked —
     which invites tapping Checkout while the add is still in flight. Submitting
     then would reach Shopify before the item does and open an empty checkout.
     The submit listener below parks that click; this releases it once the server
     has caught up. */
  function maybeCheckout() {
    if (!pendingCheckout || !pipelineIdle()) return;
    pendingCheckout = false;
    if (!serverCart || !serverCart.item_count) return;
    var form = bar && bar.querySelector('.imperia-cart-bar__form');
    if (form) form.submit();
  }

  function currentQtyOf(variantId) {
    var qty = 0;
    ((lastCart && lastCart.items) || []).forEach(function (item) {
      if (String(item.variant_id) === String(variantId)) qty = item.quantity;
    });
    return qty;
  }

  /* Repaint immediately from a locally-adjusted cart. Unit price comes from the line
     itself; a brand-new line renders with price 0 and the bar total catches up when
     the server responds — the count and the button are what the eye is waiting on. */
  function optimisticSet(variantId, handle, targetQty) {
    if (!lastCart) return;
    var cart = JSON.parse(JSON.stringify(lastCart));
    cart.items = cart.items || [];
    var item = null;
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === String(variantId)) { item = cart.items[i]; break; }
    }
    if (item) {
      var delta = targetQty - item.quantity;
      cart.total_price = Math.max(0, (cart.total_price || 0) + (item.price || 0) * delta);
      item.quantity = targetQty;
      if (targetQty <= 0) {
        cart.items = cart.items.filter(function (x) { return x !== item; });
      }
    } else if (targetQty > 0) {
      cart.items.push({ variant_id: Number(variantId), handle: handle || null, quantity: targetQty, price: 0 });
    }
    cart.item_count = 0;
    cart.items.forEach(function (x) { cart.item_count += x.quantity; });
    render(cart);
  }

  function requestSet(variantId, handle, targetQty) {
    optimisticSet(variantId, handle, targetQty);
    desired[String(variantId)] = targetQty;
    pump();
  }

  function pump() {
    if (inflight) return;
    var ids = Object.keys(desired);
    if (!ids.length) return;
    var id = ids[0];
    var target = desired[id];
    delete desired[id];
    inflight = true;

    /* change.js only touches lines that already exist server-side; a fresh line
       needs add.js. serverCart is the authority on which is which. */
    var exists = !!(serverCart && (serverCart.items || []).some(function (item) {
      return String(item.variant_id) === id;
    }));

    var req;
    if (exists) {
      req = fetch(ROOT + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: id, quantity: target })
      }).then(function (r) { return r.json(); });
    } else if (target > 0) {
      req = fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: target }] })
      }).then(function () { return getCart(); });
    } else {
      /* Removing something the server never had — nothing to send */
      inflight = false;
      pump();
      return;
    }

    req
      .then(function (cart) {
        inflight = false;
        if (cart && cart.items) serverCart = cart;
        if (Object.keys(desired).length) {
          /* More clicks arrived while this was in flight — keep the optimistic
             pixels on screen and send the newest target before repainting. */
          pump();
        } else if (cart && cart.items) {
          announce(cart);
          maybeCheckout();
        } else {
          /* change.js on a line the server no longer has answers with an error
             object, not a cart — resync rather than leave the screen ahead of
             the server. */
          getCart().then(function (c) { serverCart = c; announce(c); maybeCheckout(); });
        }
      })
      .catch(function () {
        /* Network hiccup: throw away the optimistic state and resync to truth */
        inflight = false;
        desired = {};
        getCart().then(function (cart) { serverCart = cart; announce(cart); maybeCheckout(); });
      });
  }

  /* ------------------------------------------------------------------ adding */

  var productCache = {};

  function productData(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch(ROOT + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
    return productCache[handle];
  }

  /* Resolve which variant the card is offering: the hidden input on single-variant
     cards, otherwise the checked colour swatch, otherwise the first available one. */
  function resolveVariant(holder) {
    var input = holder.querySelector('input[name="id"]');
    if (input && input.value) return Promise.resolve(input.value);

    var card = holder.closest('.product-card');
    var swatch = card && card.querySelector('input[data-variant-id]:checked');
    if (swatch && swatch.getAttribute('data-variant-id')) {
      return Promise.resolve(swatch.getAttribute('data-variant-id'));
    }

    var handle = handleOf(holder);
    if (!handle) return Promise.resolve(null);
    return productData(handle).then(function (product) {
      var available = (product.variants || []).filter(function (v) { return v.available; });
      return available.length ? String(available[0].id) : null;
    });
  }

  function addToCart(holder) {
    var handle = handleOf(holder);
    resolveVariant(holder).then(function (variantId) {
      if (!variantId) return;
      requestSet(variantId, handle, currentQtyOf(variantId) + 1);
    });
  }

  /* -------------------------------------------------------------------- events */

  /* Capture phase: this has to run before Impact's own handlers so the quick-buy
     variant drawer and the default form submit never fire. Adding is silent. */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var stickyAtc = target.closest('.imperia-cart-bar__atc');
    if (stickyAtc) {
      var mainInput = mainVariantInput();
      if (mainInput && mainInput.value) {
        requestSet(mainInput.value, PRODUCT_HANDLE, currentQtyOf(mainInput.value) + 1);
      }
      return;
    }

    var step = target.closest('.imperia-buy-bar__step');
    if (step) {
      event.preventDefault();
      event.stopPropagation();
      var variantId = step.getAttribute('data-variant');
      var delta = parseInt(step.getAttribute('data-step'), 10) || 0;
      requestSet(variantId, null, Math.max(0, currentQtyOf(variantId) + delta));
      return;
    }

    var button = target.closest('.imperia-buy-bar__button');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    if (button.classList.contains('is-in-cart')) return;
    var holder = button.closest('.imperia-buy-bar');
    if (holder) addToCart(holder);
  }, true);

  /* Hold the checkout submit while cart writes are in flight; maybeCheckout
     releases it the moment the pipeline drains. Capture phase so it runs before
     the browser commits to the navigation. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.classList || !form.classList.contains('imperia-cart-bar__form')) return;
    if (!pipelineIdle()) {
      event.preventDefault();
      pendingCheckout = true;
    }
  }, true);

  /* Variant pickers are radio/select inputs whose change bubbles; Impact then syncs
     the main form's hidden id input. Re-reading the price on the next frame keeps the
     sticky bar's price in step with the selected variant. */
  document.addEventListener('change', function (event) {
    if (!PRODUCT_HANDLE || !event.target || !event.target.closest) return;
    if (event.target.closest('variant-picker, form[is="product-form"]')) {
      requestAnimationFrame(refreshAtcPrice);
    }
  });

  document.addEventListener('cart:change', function (event) {
    if (event.detail && event.detail.cart) {
      serverCart = event.detail.cart;
      render(event.detail.cart);
    }
  });

  document.addEventListener('cart:refresh', function () {
    if (selfRefresh) return;
    getCart().then(function (cart) { serverCart = cart; render(cart); });
  });

  /* The drawer is authored inside the header section, whose ancestors are sticky/transformed —
     that makes position: fixed resolve against the header rather than the viewport, so the
     drawer was only ever as tall as its container. Reparenting to <body> fixes it for good. */
  function liftDrawer() {
    var drawer = document.getElementById('imperia-mobile-menu');
    if (drawer && drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }
  }

  /* Point the header hamburger at the Imperia drawer instead of Impact's default one */
  function adoptHamburger() {
    if (!document.getElementById('imperia-mobile-menu')) return;
    document.querySelectorAll('[aria-controls="header-sidebar-menu"]').forEach(function (trigger) {
      trigger.setAttribute('aria-controls', 'imperia-mobile-menu');
    });
  }

  /* The buy bar no longer opens Impact's quick-buy dialog, so drop the ARIA that says it does */
  function dropQuickBuyAria() {
    document.querySelectorAll('.imperia-buy-bar__button[aria-controls]').forEach(function (button) {
      button.removeAttribute('aria-controls');
      button.removeAttribute('aria-expanded');
    });
  }

  function init() {
    liftDrawer();
    adoptHamburger();
    dropQuickBuyAria();
    getCart().then(function (cart) { serverCart = cart; render(cart); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) getCart().then(function (cart) { serverCart = cart; render(cart); });
  });
})();
